/** @jsxImportSource @emotion/react */
import {
  Box,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionDetailsProps as MuiAccordionDetailsProps,
  AccordionProps as MuiAccordionProps,
  AccordionSummary as MuiAccordionSummary,
  AccordionSummaryProps as MuiAccordionSummaryProps,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useEffect, useMemo, useState } from 'react'

import { draggableAccordionTitle } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/LayersOrganizer'
import { accordionStateAtom } from '@app/risk-migration/atoms/accordionStateAtom'
import { useAtom } from 'jotai'
import { Icon } from '../Icon'
import { SortableList } from '../Sortable'
import { accordionClass } from './UiclAccordion.styles'

export interface UiclAccordionProps extends Omit<MuiAccordionProps, 'children'> {
  header: JSX.Element
  body: JSX.Element
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void
  accordionStateKey?: string
  fullHeight?: boolean
  disableExpand?: boolean
  isDraggable?: boolean
  lazyContent?: boolean
  className?: string
  expanded?: boolean // controlled prop
  defaultExpandedOnce?: boolean
  toggleButton?: boolean
}

export const UiclAccordion = ({
  header,
  body,
  accordionStateKey,
  className,
  onChange,
  toggleButton,
  expanded: controlledExpanded,
  fullHeight = false,
  disableExpand = false,
  isDraggable = false,
  lazyContent = false,
  defaultExpanded = false,
  defaultExpandedOnce = false,
  ...rest
}: UiclAccordionProps) => {
  const theme = useTheme()

  const [accordionState, setAccordionState] = useAtom(accordionStateAtom)

  const setAccordionOpenState = (accordionId: string, openState: boolean) => {
    setAccordionState((previousState) => ({
      ...previousState,
      [accordionId]: openState,
    }))
  }
  let expandedState = defaultExpanded
  if (accordionStateKey !== undefined && accordionState[accordionStateKey])
    expandedState = accordionState[accordionStateKey]
  else if (
    defaultExpandedOnce &&
    accordionStateKey !== undefined &&
    accordionState[accordionStateKey] === undefined
  ) {
    expandedState = true
  }

  const [expandedInternal, setExpandedInternal] = useState(expandedState)
  const expanded = useMemo(() => {
    return controlledExpanded === undefined ? expandedInternal : controlledExpanded
  }, [controlledExpanded, expandedInternal])

  const handleChange = (_event: React.SyntheticEvent, expanded: boolean) => {
    if (onChange) onChange(_event, expanded)
    if (disableExpand) return
    setExpandedInternal(expanded)
    if (accordionStateKey !== undefined) setAccordionOpenState(accordionStateKey, expanded)
  }

  useEffect(() => {
    if (
      defaultExpandedOnce &&
      accordionStateKey !== undefined &&
      accordionState[accordionStateKey] !== expanded
    ) {
      setAccordionOpenState(accordionStateKey, expanded)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isDraggable) {
    return (
      <MuiAccordion
        {...rest}
        css={accordionClass({ theme, fullHeight, isExpanded: expanded, disableExpand })}
        expanded={controlledExpanded === undefined ? expanded : controlledExpanded}
        onChange={handleChange}
        elevation={0}
        className={className}
        slotProps={{
          transition: {
            unmountOnExit: lazyContent,
            mountOnEnter: lazyContent,
          },
        }}
        disableGutters
      >
        <UiclAccordionSummary
          aria-controls={`accordion-header`}
          id={`accordion-header`}
          disableExpand={disableExpand}
        >
          <Box css={draggableAccordionTitle}>
            <SortableList.DragHandle />
            {header}
          </Box>
        </UiclAccordionSummary>
        <UiclAccordionDetails aria-controls={`accordion-content`} id={`accordion-header`}>
          {body}
        </UiclAccordionDetails>
      </MuiAccordion>
    )
  }

  return (
    <MuiAccordion
      {...rest}
      css={accordionClass({ theme, fullHeight, isExpanded: expanded, disableExpand })}
      expanded={controlledExpanded === undefined ? expanded : controlledExpanded}
      onChange={handleChange}
      elevation={0}
      disableGutters
      className={className}
      slotProps={{
        transition: {
          unmountOnExit: lazyContent,
          mountOnEnter: lazyContent,
        },
      }}
    >
      <UiclAccordionSummary
        aria-controls={`accordion-header`}
        id={`accordion-header`}
        disableExpand={disableExpand}
        expandIcon={toggleButton ? null : <Icon iconName="ChevronDown" />}
      >
        {header}
      </UiclAccordionSummary>
      <UiclAccordionDetails aria-controls={`accordion-content`} id={`accordion-details`}>
        {body}
      </UiclAccordionDetails>
    </MuiAccordion>
  )
}

export interface UiclAccordionSummaryProps extends MuiAccordionSummaryProps {
  disableExpand?: boolean
}

export const UiclAccordionSummary = ({ disableExpand, ...rest }: UiclAccordionSummaryProps) => {
  const theme = useTheme()

  return (
    <MuiAccordionSummary
      data-test-id="AccordionSummary"
      expandIcon={
        !disableExpand && (
          <Icon iconName="AccordionArrowDown" colour={theme.palette.grey[600]} size="small" />
        )
      }
      {...rest}
    />
  )
}

export type UiclAccordionDetailsProps = MuiAccordionDetailsProps

export const UiclAccordionDetails = ({ ...rest }: UiclAccordionDetailsProps) => {
  return <MuiAccordionDetails data-test-id="AccordionDetails" {...rest} />
}
